import { computed, defineComponent } from "vue";
import { mapState } from "pinia";
import { useApp } from "../../../App/useApp";
import moment from "moment";
import { Container } from "aurelia-dependency-injection";

import { formatDate, formatDateInput } from "../../../../common/vue-helpers/modifiers/value-modifier";
import { ApplicationEnum, ApplicationNameEnum } from "../../../../enums/ApplicationEnum";
import { IGetAgency, IGetOrganization } from "../../../../services/agency.service";
import config from "../../../../common/config";
import { BranchesService } from "../../../../services/branches-service";
import { IMultiSelectDropDownOption } from "../../../../resources-vue/vue-interfaces/i-grouped-multiselect";
import { PermissionManager } from "../../../../common/utilities/permission-manager";
import { ParentPermissionEnum } from "../../../../enums/ParentPermissionEnum";
import { PermissionActionEnum } from "../../../../enums/PermissionActionEnum";
import { TaskNameEnum } from "../../../../enums/TaskNameEnum";
import { IBranchResponse } from "../../../../resources-vue/vue-interfaces/i-branch";
import {
    IUnassignedPatientVisit,
    IUnassignedPatientVisitData,
    IUnassignedPatientVisitRequest,
    IUnassignedPatientVisitResponse
} from "../../../../resources-vue/vue-interfaces/i-task";
import { ITypeaheadOptions } from "../../../../resources-vue/vue-interfaces/i-typeahead";
import { IPageCallbackResult } from "../../../../resources-vue/vue-interfaces/i-page-container";
import { ToastrService } from "../../../../services/toastr.service";
import { PAGE_FAIL_RESPONSE } from "../../../CentralIntake/Inquiry/CentralInquiry";
import { IReassignVisitDetails } from "../../../../resources-vue/vue-interfaces/i-reassign-visit";
import { TaskService } from "../../../../services/task.service";
import { AnalyticsCategory, AnalyticsService } from "../../../../services/analytics.service";

import DatePicker from "../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import MultiSelect from "../../../../resources-vue/vue-elements/MultiSelect/MultiSelect.vue";
import MultiSelectFilter from "../../../../resources-vue/vue-elements/MultiSelectFilter/MultiSelectFilter.vue";
import Pagination from "../../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import EmployeeBulkReassign from "../../../../resources-vue/vue-forms/ReassignForms/EmployeeBulkReassign/EmployeeBulkReassign.vue";
import PageLayout from "../../../../resources-vue/vue-custom-elements/PageLayout/PageLayout.vue";
import SortableTh from "../../../../resources-vue/vue-custom-elements/SortableTh/SortableTh.vue";
import EmptyTextHandler from "../../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";
import ReassignVisit from "../../../../resources-vue/vue-dialogs/ReassignVisit/ReassignVisit.vue";
import ErrorMessageHandler from "../../../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";

export default defineComponent({
    components: {
        DatePicker,
        MultiSelect,
        MultiSelectFilter,
        EmployeeBulkReassign,
        PageLayout,
        SortableTh,
        EmptyTextHandler,
        Pagination,
        ErrorMessageHandler
    },

    props: {
        application: { typeof: ApplicationEnum, default: null },
        getUnassignedPatientsVisits: { typeof: Function, default: null },
        exportUnassignedPatientsVisits: { typeof: Function, default: null },
        getAllTasks: { typeof: Function, default: null }
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    data(vm) {
        return {
            _analyticsServices: null as AnalyticsService,
            _toastrService: null as ToastrService,
            _permissionManager: null as PermissionManager,
            _branchService: null as BranchesService,
            _taskService: null as TaskService,
            dateFrom: moment().format("MM/DD/YYYY") as string,
            dateTo: moment().add(7, "days").format("MM/DD/YYYY") as string,
            selectOrganization: [] as IGetOrganization[],
            agencyList: [] as IGetAgency[],
            branches: [],
            agencyMultiSelectOptions: {} as IMultiSelectDropDownOption,
            selectedAgencies: [] as string[],
            branchMultiSelectOptions: {} as IMultiSelectDropDownOption,
            selectedBranches: [] as string[],
            branchList: [] as IBranchResponse[],
            page: null as IUnassignedPatientVisitData,
            selectedTasks: [] as ITypeaheadOptions[],
            selectedVisits: [] as IUnassignedPatientVisit[],
            isError: false,
            unassignedVisitsHOSPALPagination: null,
            isAddItemRequested: false,
            moveListDown: false,
            bulkReassignEnabled: false,
            tooltip: `Bulk Assign is enabled when two or more visits are selected for one specific Agency and Branch.`,
            isLoading: false,
            columns: 10,
            pageSize: 10,
            oldPageNumber: 1,
            newPageNumber: 1,
            responseHandler: {
                data: null,
                stamp: null,
                requests: []
            },
            sortBy: "userFirstName",
            sortOrder: "asc",
            customIds: [] as string[],
            selectAll: false,
            dialogRef: null,
            tasksList: [],
            exportFilter: null as IUnassignedPatientVisitRequest,
            isExporting: false as boolean,
            formatDateInput,
            formatDate,
            moment,
            taskFirstLoaded: false
        };
    },

    async created() {
        this._branchService = Container.instance.get(BranchesService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._toastrService = Container.instance.get(ToastrService);
        this._taskService = Container.instance.get(TaskService);
        this._analyticsServices = Container.instance.get(AnalyticsService);
        this.unassignedVisitsHOSPALPagination = this.$refs?.unassignedVisitsHOSPALPagination as typeof Pagination;
        this.isLoading = true;
    },

    async mounted() {
        const organizationId = localStorage.getItem(config.organizationId);
        this.selectOrganization = this.organizations.filter((organization) => organization.id == organizationId);
        const selectedAgencies = this.selectOrganization[0].agencies.filter(
            (agency) => agency.application === this.application
        );
        this.agencyList = selectedAgencies.map((agency) => {
            const agencyAndApplicationIds = agency.id.split("_");
            let application = parseInt(agencyAndApplicationIds[1], 10);
            if (application == 256) {
                application = ApplicationEnum.AxxessHospice;
            }
            return {
                ...agency,
                agencyId: agencyAndApplicationIds[0],
                application: application
            };
        });
        this.branches = await this._branchService.getAllBranches();
        this.branchList = this.branches.filter((branch) => branch.application == this.application);
        await this.setAgencyMultiSelect();
        await this.setBranchMultiSelect();
        this.tasksList = await this.tasksFetch();
        if (this.$route.query.agencies) {
            const agencies = (this.$route.query.agencies as string).split(",");
            this.updateBranchesbyAgency(agencies);
        }
        if (this.$route.query.dateTo) {
            this.changeDate(String(this.$route.query.dateTo), "dateTo");
        }
        if (this.$route.query.dateFrom) {
            this.changeDate(String(this.$route.query.dateFrom), "dateFrom");
        }
        if (this.$route.query.task) {
            const queryTasks = (this.$route.query.task as string).split(",");
            const selectedTasks = [];
            queryTasks.forEach((task) => {
                this.tasksList.forEach((taskData) => {
                    if (taskData.value === task) {
                        selectedTasks.push(taskData);
                    }
                });
            });
            this.selectedItemsUpdated(selectedTasks);
        }

        this.unassignedVisitsHOSPALPagination = this.$refs?.unassignedVisitsHOSPALPagination as typeof Pagination;
        this.pageReset();
    },

    watch: {
        selectedAgencies: {
            handler() {
                this.setBranchMultiSelect();
            },
            deep: true
        },
        selectedVisits() {
            this.updateSelectAll();
            if (this.selectedVisits?.length >= 2) {
                const checkBranch = this.selectedVisits[0].BranchId;
                const isSameBranch = this.selectedVisits.every((task) => task.BranchId === checkBranch);
                if (isSameBranch) {
                    this.bulkReassignEnabled = true;
                } else {
                    this.bulkReassignEnabled = false;
                }
            } else {
                this.bulkReassignEnabled = false;
            }
        },
        page: {
            deep: true,
            handler(newValue) {
                if (newValue?.Items?.length > 0) {
                    this.$nextTick(() => {
                        const multiSelectFilter = this.$refs.tasksFilter as any;
                        if (multiSelectFilter && multiSelectFilter.search) {
                            multiSelectFilter.onCredentialsFetch();
                        }
                    });
                }
            }
        },
        async taskNameDependeny() {
            if (this.taskFirstLoaded) {
                this.tasksList = await this.tasksFetch();
            }
        }
    },

    computed: {
        ...mapState(useApp, ["organizations"]),
        firstAgencyId() {
            const matchedAgencyId = this.selectedVisits
                .map((task) => this.agencyList.find((agency) => agency.name === task.AccountName)?.agencyId)
                .find((id) => id);
            const agencyAndApplicationIds = this.selectedAgencies[0].split("_");
            return matchedAgencyId || agencyAndApplicationIds[0];
        },
        applicationName() {
            let application = this.application;
            if (application === ApplicationEnum.AxxessHospiceFE) {
                application = ApplicationEnum.AxxessHospice;
            }
            return ApplicationNameEnum[application];
        },
        unassignedVisitsLabel(): string {
            return this.application === ApplicationEnum.HomeCare
                ? " Unassigned Clients Visits"
                : " Unassigned Patients Visits";
        },
        hasData() {
            return this.page?.Items.length > 0;
        },
        loadingCount() {
            return this.page?.Items?.length > 0 ? this.page.Items.length : config.pageSize;
        },
        taskNameDependeny() {
            return JSON.stringify([this.dateFrom, this.dateTo, this.application, this.selectedBranches]);
        }
    },

    methods: {
        changeDate(date: string, name: string) {
            if (name == "dateFrom") {
                this.dateFrom = date;

                this.$router.push({
                    query: {
                        application: this.$route.query.application,
                        agencies: this.$route.query.agencies,
                        dateFrom: date,
                        dateTo: this.$route.query.dateTo
                    }
                });
            }
            if (name == "dateTo") {
                this.dateTo = date;
                this.$router.push({
                    query: {
                        application: this.$route.query.application,
                        agencies: this.$route.query.agencies,
                        dateFrom: this.$route.query.dateFrom,
                        dateTo: date
                    }
                });
            }
        },
        handleDateInputChange(e: any) {
            const name = e.target.name;
            const value = e.target.value;
            this.changeDate(value, name);
        },
        async setAgencyMultiSelect() {
            this.agencyMultiSelectOptions = {};
            this.selectedAgencies.length = 0;
            if (!this.agencyList || this.agencyList?.length === 0) {
                return;
            }
            const application =
                this.application == ApplicationEnum.AxxessHospiceFE ? ApplicationEnum.AxxessHospice : this.application;
            const allowedProducts = [application];
            const schedulerViewPermissions = this._permissionManager.getPermmission(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canView
            );
            const schedulerAgencies = schedulerViewPermissions
                .filter((item: any) => {
                    if (!item) {
                        return false;
                    }
                    let currentapplication = item.application;
                    if (currentapplication === ApplicationEnum.AxxessHospiceFE) {
                        currentapplication = ApplicationEnum.AxxessHospice;
                    }
                    return allowedProducts.includes(currentapplication);
                })
                .map((x: any) => x.agencyId);
            const filterableProducts = this.agencyList.filter((agency) => {
                let isAllowed = allowedProducts.includes(agency.application);
                return isAllowed && schedulerAgencies.includes(agency.agencyId);
            });
            filterableProducts?.forEach((agency) => {
                const appName =
                    ApplicationNameEnum[
                        agency.application === ApplicationEnum.AxxessHospiceFE
                            ? ApplicationEnum.AxxessHospice
                            : agency.application
                    ];

                if (!this.agencyMultiSelectOptions[appName]) {
                    this.agencyMultiSelectOptions[appName] = [];
                }

                this.agencyMultiSelectOptions[appName].push({
                    displayText: agency.name,
                    value: agency.id
                });
            });
            Object.values(this.agencyMultiSelectOptions).forEach((agency: any) => {
                const values = agency.map((item: any) => item.value);
                this.selectedAgencies.push(...values);
            });
        },
        updateBranchesbyAgency(branches: any) {
            this.selectedAgencies.length = 0;
            this.selectedAgencies.push(...branches);
            const queryAgencies = this.selectedAgencies.filter((_, index: number) => index < 12);
            this.$router.push({
                query: { application: this.$route.query.application, agencies: queryAgencies.join(",") }
            });
        },
        async setBranchMultiSelect() {
            try {
                this.branchMultiSelectOptions = {};
                this.selectedBranches = [];
                if (!this.branchList || this.branchList?.length === 0) {
                    return;
                }
                const allowedProducts = [this.application];
                const schedulerViewPermissions = this._permissionManager.getPermmission(
                    ParentPermissionEnum.scheduler,
                    PermissionActionEnum.canView
                );

                const selectedAgencies = this.selectedAgencies.map((agency: any) => agency.split("_")[0]);

                const schedulerAgencies = schedulerViewPermissions
                    .filter((item: any) => {
                        if (!item) {
                            return false;
                        }
                        const application = item.application;
                        return allowedProducts.includes(application) && selectedAgencies.includes(item.agencyId);
                    })
                    .map((x: any) => x.agencyId);
                const filterableProducts = this.branchList.filter((branch) => {
                    const isAllowed = allowedProducts.includes(branch.application);
                    return isAllowed && schedulerAgencies.includes(branch.accountId);
                });
                this.agencyList.forEach((agency: any) => {
                    if (!this.branchMultiSelectOptions[agency.name]) {
                        this.branchMultiSelectOptions[agency.name] = [];
                    }
                    filterableProducts.forEach((branch) => {
                        if (branch.accountId === agency.agencyId && branch.providerName === agency.name) {
                            this.branchMultiSelectOptions[agency.name].push({
                                displayText: branch.name,
                                value: branch.id
                            });
                            this.selectedBranches.push(branch.id);
                        }
                    });
                });
            } catch (e) {
                console.warn(e);
            }
        },
        resetDateRange() {
            this.dateFrom = moment().format("MM/DD/YYYY");
            this.dateTo = moment().add(7, "days").format("MM/DD/YYYY");
        },
        updateBranches(branches: any) {
            this.selectedBranches = [];
            this.selectedBranches.push(...branches);
        },
        async tasksFetch(filter?: string) {
            const uniqueOptions = new Map();
            if (this.dateFrom && this.dateTo !== null) {
                const payload = {
                    startDate: moment(this.dateFrom).format("MM/DD/YYYY"),
                    endDate: moment(this.dateTo).format("MM/DD/YYYY"),
                    accountIds: this.selectedAgencies.map((agency) => agency.split("_")[0]),
                    application: this.application,
                    locationIds: this.selectedBranches,
                    defaultLocationIds: this.selectedBranches,
                    statuses: [TaskNameEnum.unassignedVisit],
                    providerIds: this.selectedAgencies
                        .map((agency) => (agency.split("_").length > 2 ? agency.split("_")[2] : ""))
                        ?.filter((x) => x !== "")
                };
                const response = await this._taskService.fetchAllTask(payload);
                response.Data.forEach((task: any) => {
                    if (!uniqueOptions.has(task.TaskNames)) {
                        uniqueOptions.set(task.TaskNames, {
                            name: task.TaskNames,
                            value: task.Id
                        });
                    }
                });
                this.taskFirstLoaded = true;
            }
            const result = Array.from(uniqueOptions.values()).filter(
                (x) => !filter || x.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
            );
            return result;
        },
        getTasksList() {
            return this.tasksList;
        },
        selectedItemsUpdated(options: ITypeaheadOptions[]) {
            this.selectedTasks = options;
            const queryTasks = this.selectedTasks.filter((_, index: number) => index < 12);
            this.$router.push({
                query: {
                    application: this.$route.query.application,
                    agencies: this.$route.query.agencies,
                    dateFrom: this.$route.query.dateFrom,
                    dateTo: this.$route.query.dateTo,
                    task: queryTasks.map((task) => task.value).join(",")
                }
            });
        },
        async pageReset(clearSelectedTask: boolean = false) {
            if (clearSelectedTask) {
                this.selectedVisits = [] as IUnassignedPatientVisit[];
            }
            this.isError = false;
            this.unassignedVisitsHOSPALPagination?.reset();
        },
        async clearFilters() {
            this.selectedItemsUpdated([]);
            this.selectedVisits = [] as IUnassignedPatientVisit[];
            await this.setAgencyMultiSelect();
            await this.setBranchMultiSelect();
            this.resetDateRange();
            this.pageReset();
            this.$router.push({
                query: {
                    application: this.$route.query.application,
                    agencies: "",
                    dateFrom: "",
                    dateTo: "",
                    task: ""
                }
            });
        },
        checkedTask() {
            return this.selectedVisits.map((task) => task.TaskId);
        },
        closeReassignForm() {
            this.isAddItemRequested = false;
            this.moveListDown = false;
            this.selectedVisits = [] as IUnassignedPatientVisit[];
        },
        openReassignForm() {
            this.isAddItemRequested = true;
            this.moveListDown = true;
        },
        async pageChangedCallback(pageNumber: number, pageSize: number): Promise<IPageCallbackResult> {
            if (this.selectedBranches.length == 0) {
                this._toastrService.error({
                    title: `Error`,
                    message: `Select at least one agency and branch to proceed.`
                });
                return PAGE_FAIL_RESPONSE;
            }
            try {
                this.oldPageNumber = this.newPageNumber;
                this.newPageNumber = pageNumber;
                const callTime = Date.now();
                const taskIds = this.selectedTasks.map((option) => {
                    return option.value;
                });
                this.responseHandler.stamp = callTime;
                this.isLoading = true;
                this.responseHandler.data = null;
                this.isError = false;
                const payload: IUnassignedPatientVisitRequest = {
                    startDate: moment(this.dateFrom).format("MM/DD/YYYY"),
                    endDate: moment(this.dateTo).format("MM/DD/YYYY"),
                    teamIds: [],
                    tagsIds: [],
                    page: pageNumber,
                    pageLength: pageSize,
                    sortBy: this.sortBy,
                    order: this.sortOrder,
                    accountIds: this.selectedAgencies.map((agency) => agency.split("_")[0]),
                    application: this.application,
                    userId: "",
                    locationIds: this.selectedBranches,
                    defaultLocationIds: this.selectedBranches,
                    providerIds: this.selectedAgencies
                        .map((agency) => (agency.split("_").length > 2 ? agency.split("_")[2] : ""))
                        ?.filter((x) => x !== "")
                };
                if (this.application === ApplicationEnum.HomeCare) {
                    payload.HCTaskIdFilter = taskIds;
                } else {
                    payload.taskIdFilter = taskIds;
                }
                this.exportFilter = { ...payload };
                const response = this.getUnassignedPatientsVisits(payload, this.application);
                this.responseHandler.requests.push(response);
                const responseData: IUnassignedPatientVisitResponse = await response;
                let data = responseData.data;
                if (this.responseHandler.stamp == callTime) {
                    this.responseHandler.data = data;
                } else {
                    data =
                        this.responseHandler.data ||
                        (await this.responseHandler.requests[this.responseHandler.requests.length - 1]);
                }
                this.page = data;
                this.customIds = data.Items.map((item) => {
                    return item.CustomTaskId;
                });
                this.updateSelectAll();
                return {
                    totalPageCount: data.PageCount,
                    success: data.ItemCount > 0,
                    totalCount: data.ItemCount
                };
            } catch (e) {
                this.isError = true;
                return PAGE_FAIL_RESPONSE;
            } finally {
                this.isLoading = false;
            }
        },

        async exportVisits() {
            this.isExporting = true;
            this._analyticsServices.logEvent({
                category: AnalyticsCategory.ExporttoExcelforUnassignedPatientVisits,
                action: `Export for Unassigned Visits-${ApplicationNameEnum[Number(this.application)]}`
            });
            await this.exportUnassignedPatientsVisits(this.exportFilter);
            this.isExporting = false;
        },

        updateSelectAll() {
            if (this.page?.Items?.length) {
                if (
                    this.selectedVisits.length === this.page?.Items.length &&
                    this.oldPageNumber === this.newPageNumber
                ) {
                    this.selectAll = true;
                } else if (
                    this.oldPageNumber !== this.newPageNumber &&
                    this.page.Items.every((item) => this.checkedTask().includes(item.TaskId))
                ) {
                    this.selectAll = true;
                } else {
                    this.selectAll = false;
                }
            }
        },
        changeAllSelection() {
            if (!this.selectAll) {
                const taskSet = new Set(this.page.Items.map((item) => item.TaskId));
                this.selectedVisits = this.selectedVisits.filter((visit) => !taskSet.has(visit.TaskId));
            } else {
                const allVisits = JSON.parse(JSON.stringify(this.selectedVisits));
                this.page.Items.forEach((task) => {
                    if (!new Set(this.checkedTask()).has(task.TaskId)) {
                        allVisits.push(task);
                    }
                });
                this.selectedVisits = allVisits;
            }
        },
        sortByUpdated(value: string) {
            if (value != this.sortBy) {
                this.sortBy = value;
                this.pageReset();
            }
        },
        sortOrderUpdated(value: string) {
            if (value != this.sortOrder) {
                this.sortOrder = value;
                this.pageReset();
            }
        },
        updatePageSizeChanged(value: number) {
            this.pageSize = value;
        },
        async scheduledVisit(task: IUnassignedPatientVisit) {
            const currentBranch = this.branches.find((branch) => branch.id === task.BranchId);
            const currentAgencyId = currentBranch.accountId;

            const visitDetails: IReassignVisitDetails = {
                CustomTaskId: task.CustomTaskId,
                AgencyId: currentAgencyId,
                Application: this.application,
                EpisodeId: "",
                PatientId: task.PatientId,
                Id: task.TaskId,
                LocationId: task.BranchId,
                TaskName: task.VisitType,
                FirstName: task.FirstName,
                LastName: task.LastName,
                StartDate: task.VisitDate,
                fromUnassignedPage: true
            };

            this.dialogRef = this.$dialog.open(ReassignVisit, {
                props: {
                    modal: true,
                    showHeader: false,
                    contentStyle: {
                        minWidth: "650px",
                        minHeight: visitDetails.Application === ApplicationEnum.HomeCare ? "350px" : "280px"
                    }
                },
                data: {
                    visitDetails
                },
                onClose: async (options) => {
                    if (options.data === "success") {
                        this.pageReset();
                    }
                }
            });
        },
        handleFindNearByEmployees(task: IUnassignedPatientVisit) {
            const agencyId = this.branchList.find((branch: any) => branch.id === task.BranchId).accountId;
            this._analyticsServices.logEvent({
                category: AnalyticsCategory.AxxessPlannerVisits,
                action: "Unassigned-FindNearByEmployees"
            });
            this.$router.push({
                path: `/axxess-central/schedule-center/unassigned-patient-visits/map`,
                state: {
                    BranchId: task.BranchId,
                    MiddleInitial: task.MiddleInitial,
                    FirstName: task.FirstName,
                    LastName: task.LastName,
                    PatientId: task.PatientId,
                    TaskId: task.TaskId,
                    Status: task.Status,
                    VisitDate: task.VisitDate,
                    Application: task.Application,
                    EvvIconStatus: task.EvvIconStatus,
                    PatientAddress: JSON.stringify(task.PatientAddress),
                    VerificationVisitStatus: task.VerificationVisitStatus,
                    StartDateTime: task.StartDateTime,
                    EndDateTime: task.EndDateTime,
                    agencyId: agencyId,
                    taskName: task.VisitType,
                    CustomTaskId: task.CustomTaskId
                }
            });
        }
    }
});
